import { createFileRoute } from "@tanstack/react-router";
import { useState, ChangeEvent } from "react";
import useTranslation from "../hooks/useTranslation";

export const Route = createFileRoute("/learn-together")({
  component: RouteComponent,
});

function RouteComponent() {
  const { translate } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [inviteEmail, setInviteEmail] = useState("");
  const inviteLink = "https://deine-seite.de/invite?token=abc123";

  const handleNext = () => {
    if (step === 1 && selectedOption) setStep(2);
    else if (step === 2 && selectedPlan) setStep(3);
    else if (
      step === 3 &&
      formData.name &&
      formData.email &&
      formData.password
    ) {
      if (selectedOption === "each_pays") setStep(4);
      else alert(translate("alert_registration_complete"));
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {step === 1 && (
        <>
          <h1 className="text-3xl font-bold text-center">
            {translate("pricing_choose_how_to_start")}
          </h1>
          <p className="text-center text-gray-600">
            {translate("pricing_motivation_subline")}
          </p>

          <form className="space-y-4 mt-6">
            <label className="flex items-start gap-4 p-4 border rounded-xl cursor-pointer">
              <input
                type="radio"
                name="option"
                value="pay_for_both"
                onChange={() => setSelectedOption("pay_for_both")}
                checked={selectedOption === "pay_for_both"}
                className="mt-1"
              />
              <div>
                <div className="font-semibold text-lg">
                  {translate("option_pay_for_both_title")}
                </div>
                <div className="text-sm text-gray-500">
                  {translate("option_pay_for_both_desc")}
                </div>
              </div>
            </label>

            <label className="flex items-start gap-4 p-4 border rounded-xl cursor-pointer">
              <input
                type="radio"
                name="option"
                value="each_pays"
                onChange={() => setSelectedOption("each_pays")}
                checked={selectedOption === "each_pays"}
                className="mt-1"
              />
              <div>
                <div className="font-semibold text-lg">
                  {translate("option_each_pays_title")}
                </div>
                <div className="text-sm text-gray-500">
                  {translate("option_each_pays_desc")}
                </div>
              </div>
            </label>
          </form>

          <div className="text-center pt-4">
            <button
              onClick={handleNext}
              disabled={!selectedOption}
              className="w-full max-w-xs px-4 py-2 rounded-sm bg-blue-600 text-white disabled:bg-gray-300"
            >
              {translate("btn_continue")}
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h2 className="text-2xl font-bold text-center">
            {translate("plan_heading")}
          </h2>
          <p className="text-center text-gray-600 max-w-xl mx-auto">
            {translate("plan_intro")}
          </p>

          <form className="space-y-4 mt-6">
            <label className="flex items-start gap-4 p-4 border rounded-xl cursor-pointer">
              <input
                type="radio"
                name="plan"
                value="3_texts"
                onChange={() => setSelectedPlan("3_texts")}
                checked={selectedPlan === "3_texts"}
                className="mt-1"
              />
              <div>
                <div className="font-semibold text-lg">
                  {translate("plan_3_title")}
                </div>
                <div className="text-sm text-gray-500">
                  {translate("plan_3_desc")}
                </div>
              </div>
            </label>

            <label className="flex items-start gap-4 p-4 border rounded-xl cursor-pointer">
              <input
                type="radio"
                name="plan"
                value="10_texts"
                onChange={() => setSelectedPlan("10_texts")}
                checked={selectedPlan === "10_texts"}
                className="mt-1"
              />
              <div>
                <div className="font-semibold text-lg">
                  {translate("plan_10_title")}
                </div>
                <div className="text-sm text-gray-500">
                  {translate("plan_10_desc")}
                </div>
              </div>
            </label>

            <label className="flex items-start gap-4 p-4 border rounded-xl cursor-pointer">
              <input
                type="radio"
                name="plan"
                value="100_texts"
                onChange={() => setSelectedPlan("100_texts")}
                checked={selectedPlan === "100_texts"}
                className="mt-1"
              />
              <div>
                <div className="font-semibold text-lg">
                  {translate("plan_100_title")}
                </div>
                <div className="text-sm text-gray-500">
                  {translate("plan_100_desc")}
                </div>
              </div>
            </label>
          </form>

          <div className="text-center pt-4">
            <button
              onClick={handleNext}
              disabled={!selectedPlan}
              className="w-full max-w-xs px-4 py-2 rounded-sm bg-blue-600 text-white disabled:bg-gray-300"
            >
              {translate("btn_continue_to_register")}
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <h2 className="text-2xl font-bold text-center">
            {translate("register_heading")}
          </h2>
          <p className="text-center text-gray-600 max-w-md mx-auto">
            {translate("register_subline")}
          </p>

          <div className="space-y-4 max-w-md mx-auto mt-6">
            <input
              type="text"
              name="name"
              placeholder={translate("input_name")}
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-sm"
            />
            <input
              type="email"
              name="email"
              placeholder={translate("input_email")}
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-sm"
            />
            <input
              type="password"
              name="password"
              placeholder={translate("input_password")}
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-sm"
            />
            <button
              className="w-full px-4 py-2 rounded-sm bg-blue-600 text-white"
              onClick={handleNext}
            >
              {translate("btn_register")}
            </button>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <h2 className="text-2xl font-bold text-center">
            {translate("invite_heading")}
          </h2>
          <p className="text-center text-gray-600 max-w-md mx-auto">
            {translate("invite_subline")}
          </p>

          <div className="space-y-4 max-w-md mx-auto mt-6">
            <input
              type="email"
              placeholder={translate("input_partner_email")}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-sm"
            />
            <button className="w-full px-4 py-2 rounded-sm bg-blue-600 text-white">
              {translate("btn_send_invite")}
            </button>

            <p className="text-center text-sm text-gray-500">
              {translate("or_share_link")}
            </p>
            <input
              readOnly
              value={inviteLink}
              className="w-full px-4 py-2 border rounded-sm"
            />
            <button className="w-full px-4 py-2 rounded-sm border text-blue-600">
              {translate("btn_copy_link")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
